import { useEffect, useState, useCallback } from 'react';
import { Box, Button, Tooltip, Stack, Paper, Drawer, IconButton, Typography, FormControlLabel, Checkbox, TextField } from '@mui/material';
import { ImagePaths } from '../../../Utility/ImagePaths';
import { AmountsCard } from '../../../Components/CommonCards/CommonCards';
import { t } from 'i18next';
import { DataGrid } from '@mui/x-data-grid';
import { CommonGridToolBarWithFilterText, CommonPatientDetails } from '../../../Components/Common Components/CommonComponents';
import { AmountFormat, formatDate } from '../../../Components/CommonFunctions/CommonFunctions';
import RestAPIService from '../../../Utility/Services/RestAPIService';
import { Serviceurls } from '../../../Utility/API/Serviceurls';
import CommonDatePicker from '../../../Components/Common Components/CommonDatePicker';
import { DateTime } from 'luxon';
import ToastMsg from '../../../Components/ToastMsg/ToastMsg';
import CommonValidation from '../../../Components/CommonFunctions/CommonValidation';
import { localGetItem, localSetItem } from '../../../Utility/Services/CacheProviderService';

const CurrencySymbol = "₹";  // Adjust the currency symbol as needed

const OrderBills = () => {
    const [orderList, setOrderList] = useState([]);  // Order data
    const [cardData, setCardData] = useState({})    // Card Data

    // pagination state
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    // loading state
    const [isLoader, setIsLoader] = useState(false);

    // Drawer state management
    const [filterOpen, setFilterOpen] = useState(false);

    // Filter state management
    const savedFilter = JSON.parse(localGetItem('globalDateFilter') || 'null');
    const [filterDatas, setFilterDatas] = useState({
        FromDate: savedFilter?.FromDate ||  DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd'),
        ToDate: savedFilter?.ToDate || DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd'),
        DateIndex: savedFilter?.DateIndex || 1,
        patient_Name: "",
        UHID_No: "",
        phoneNumber: "",
        orderFromNo: "",
        orderToNo: "",
        module_type: ['IP', 'OP', 'RADIOLOGY', 'LAB', ...localGetItem("Investigation_List") ? JSON.parse(localGetItem("Investigation_List")) : []],      // All selected
        status: ['Pending', 'Billed', 'Accepted'],       // All selected
        Patient_type: ['ip', 'op', 'self'],
        MultiInvestigations: localGetItem("Investigation_List") ? JSON.parse(localGetItem("Investigation_List")) : [],
    });

    // error state
    const [isErrorMsg, setIsErrorMsg] = useState(false);
    const [isErrorMsgText, setIsErrorMsgText] = useState('');

    // error function
    const errorMessage = (message) => {
        setIsErrorMsg(true);
        setIsErrorMsgText(message);
    };

    // Checkbox handler
    const handleCheckboxChange = (field, value) => {
        setFilterDatas((prev) => {
            const currentValues = prev[field];
            const updatedValues = currentValues.includes(value)
                ? currentValues.filter((v) => v !== value)  // Remove if already selected
                : [...currentValues, value];                // Add if not selected
            return { ...prev, [field]: updatedValues };
        });
    };

    const handleDateChange = (fromDate, toDate, dateIndex) => {
        const today = DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd');
        setFilterDatas((prev) => ({
            ...prev,
            FromDate: dateIndex === 1 ? today : fromDate,
            ToDate: dateIndex === 1 ? today : toDate,
            DateIndex: dateIndex
        }));
    };

    // Drawer handlers
    const handleOpenFilter = () => setFilterOpen(true);
    const handleCloseFilter = () => {
        setFilterOpen(false)
        getOrderListData()
    };

    // API Call Function
    const fetchOrderData = async (params, isPrint = false) => {
        setIsLoader(true);
        const clinicId = JSON.parse(localGetItem("SelectedDoctorClinicId")) ? `&clinic_id=${JSON.parse(localGetItem("SelectedDoctorClinicId"))}` : "";
        try {
            const url = `${Serviceurls.ORDER_BILLS_GET}?${params}` + clinicId;
            const response = isPrint
                ? await RestAPIService.getAllPrint(url)
                : await RestAPIService.getAll(url);

            if (response?.data?.cardData) {
                setCardData(response?.data?.cardData)
            }

            if (isPrint) {                    
                const file = new Blob(
                    [response.data],
                    { type: 'application/pdf' });
                const fileURL = URL.createObjectURL(file);
                window.open(fileURL);
            }

            if (response?.data?.status === 'success') {
                const data = response?.data?.data || [];

                const uniqueData = Array.from(
                    new Map(
                        data.map((row) => [
                            `${row.order_item_number}|${row.patient_account_number}|${row.service_name}|${row.total_amount}|${row.order_line_status}`,
                            row
                        ])
                    )?.values()
                );

                setOrderList(uniqueData);
            } else {
                console.error('Failed:', response?.data?.message);
            }
        } catch (error) {
            if (error?.response?.data?.status === "fail") {
                errorMessage(error?.response?.data?.message);
            } else {
                errorMessage(error.message);
            }
        } finally {
            setIsLoader(false);
        }
    };

    const getOrderListData = (Datas = null) => {
        const filterData = Datas || filterDatas;
        const fromDate = filterData.FromDate || '';
        const toDate = filterData.ToDate || '';
        const moduleType = filterData.module_type.join(',');  // Manually encoding
        const status = filterData.status.join(',');
        const patientType = filterData.Patient_type.join(',');

        // Manually constructing the query string
        const queryString = `from_date=${fromDate}&to_date=${toDate}&patient_name=${filterData.patient_Name || ""}&uhid=${filterData.UHID_No || ""}&mobile=${filterData.phoneNumber || ""}&order_from=${filterData.orderFromNo || ""}&order_to=${filterData.orderToNo || ""}&module_type=${moduleType}&status=${status}&patient_type=${patientType}`;

        fetchOrderData(queryString);
    };

    const PrintData = () => {
        
        const fromDate = filterDatas.FromDate || '';
        const toDate = filterDatas.ToDate || '';
        const moduleType = (filterDatas.module_type.join(','));
        const status = (filterDatas.status.join(','));
        const patientType = (filterDatas.Patient_type.join(','));
        const exportType = 'pdf';

        // Manually constructing the query string
        const queryString = `from_date=${fromDate}&to_date=${toDate}&patient_name=${filterDatas.patient_Name || ""}&uhid=${filterDatas.UHID_No}&mobile=${filterDatas.phoneNumber}&order_from=${filterDatas.orderFromNo}&order_to=${filterDatas.orderToNo}&module_type=${moduleType}&status=${status}&patient_type=${patientType}&export_type=${exportType}`;

        fetchOrderData(queryString, true);
    };

    useEffect(() => {
        getOrderListData();
    }, []);

    const handleFilterApply = () => {
        localSetItem('globalDateFilter', JSON.stringify({FromDate: filterDatas.FromDate, ToDate: filterDatas.ToDate, DateIndex: filterDatas?.DateIndex  }))
        getOrderListData();
        setFilterOpen(false);
    };

    const handleFilterClear = () => {
        localSetItem('globalDateFilter', JSON.stringify({FromDate: DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd'), ToDate: DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd'), DateIndex: 1  }))
        setFilterDatas({
            FromDate: DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd'),
            ToDate: DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd'),
            DateIndex: 1,
            patient_Name: "",
            UHID_No: "",
            phoneNumber: null,
            orderFromNo: "",
            orderToNo: "",
            module_type: ['IP', 'OP', 'RADIOLOGY', 'LAB', ...localGetItem("Investigation_List") ? JSON.parse(localGetItem("Investigation_List")) : []],
            status: ['Pending', 'Billed', 'Accepted'],
            Patient_type: ['ip', 'op', 'self']
        });
        getOrderListData({
            FromDate: DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd'),
            ToDate: DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd'),
            DateIndex: 1,
            patient_Name: "",
            UHID_No: "",
            phoneNumber: null,
            orderFromNo: "",
            orderToNo: "",
            module_type: ['IP', 'OP', 'RADIOLOGY', 'LAB', ...localGetItem("Investigation_List") ? JSON.parse(localGetItem("Investigation_List")) : []],
            status: ['Pending', 'Billed', 'Accepted'],
            Patient_type: ['ip', 'op', 'self']
        })
        setFilterOpen(false);
    };

    const gridToolBar = useCallback(() => {
        let TextArray = [
            { label: "From Date", value: formatDate(filterDatas.FromDate) },
            { label: "To Date", value: formatDate(filterDatas.ToDate) },
        ]
        return (
            <Box key="order-bills-toolbar">
                <CommonGridToolBarWithFilterText isDelete={0} FilterTextArray={TextArray} filename={"Order Bills"} />
            </Box>
        )
    },[filterDatas.FromDate, filterDatas.ToDate]);

    const Filter = () => {
        return (
            <Box component={"div"} className='eMed_Filter_Billing_Container'>
                <Box component={"div"} className='eMed_Filter_Top'>
                    <Typography variant='h6' fontWeight={"600"}>{t("Filter")}</Typography>
                    <Button onClick={() => { handleCloseFilter() }} className='eMed_usrconf_btn'>
                        <img src={ImagePaths.LabCloseButton.default} alt="close" className='eMed_action_img_small' />
                    </Button>
                </Box>
                <Box component={"div"}>
                    <Box component={"div"} className='eMed_Filter_DateFilter'>
                        <Box style={{ width: "40%", }} component={"div"}>
                            <CommonDatePicker
                                FromDate={filterDatas.FromDate}
                                ToDate={filterDatas.ToDate}
                                DateIndex={filterDatas.DateIndex}
                                SelectedDates={handleDateChange}
                                HideAllMenu={true}
                            />
                        </Box>
                    </Box>
                    <Box component={"div"} display={"flex"} flexDirection={"row"} marginLeft={'1vw'}>
                        <TextField
                            className="eMed_Bill_Filter_TextFeild_small"
                            size='small'
                            label={t('Patient Name')}
                            value={filterDatas.patient_Name}
                            autoComplete='off'
                            onChange={(e) => {
                                let number = CommonValidation.ALPHA_NUM_SPLCHAR(e.target.value);
                                if (number || e.target.value === "") {
                                    let newValue = e.target.value.toUpperCase();
                                    if (filterDatas.patient_Name !== newValue) {
                                        setFilterDatas((prevstate) => ({
                                            ...prevstate,
                                            patient_Name: newValue,
                                        }));
                                    }
                                }
                            }}
                        />
                        <TextField
                            className="eMed_Bill_Filter_TextFeild_small"
                            size='small'
                            sx={{ marginLeft: '1vw' }}
                            label={`UHID`}
                            value={filterDatas.UHID_No}
                            autoComplete='off'
                            onChange={(e) => {
                                let number = CommonValidation.ALPHA_NUM_SPLCHAR(e.target.value);
                                if (number || e.target.value === "") {
                                    setFilterDatas((prevstate) => ({
                                        ...prevstate,
                                        UHID_No: e.target.value
                                    }))
                                }
                            }
                            }
                        />
                    </Box>
                    <Box component={"div"} display={"flex"} flexDirection={"row"} marginLeft={'1vw'}>
                        <TextField
                            className="eMed_Bill_Filter_TextFeild_small"
                            size='small'
                            label={`Mobile No`}
                            value={filterDatas.phoneNumber}
                            autoComplete='off'
                            onChange={(e) => {
                                let number = CommonValidation.numberOnly(e.target.value);
                                if ((number && e.target.value.length < 11) || e.target.value === "") {
                                    setFilterDatas((prevstate) => ({
                                        ...prevstate,
                                        phoneNumber: e.target.value
                                    }))
                                }
                            }
                            }
                        />
                    </Box>
                    <Box component={"div"} display={"flex"} flexDirection={"row"} marginLeft={'1vw'}>
                        <TextField
                            className="eMed_Bill_Filter_TextFeild_small"
                            size='small'
                            label={t('Order From No')}
                            value={filterDatas.orderFromNo}
                            autoComplete='off'
                            onChange={(e) => {
                                let number = CommonValidation.numberOnly(e.target.value);
                                if (number || e.target.value === "") {
                                    setFilterDatas((prevstate) => ({
                                        ...prevstate,
                                        orderFromNo: e.target.value.toUpperCase()
                                    }))
                                }
                            }}
                        />
                        <TextField
                            className="eMed_Bill_Filter_TextFeild_small"
                            size='small'
                            sx={{ marginLeft: '1vw' }}
                            label={`Order To No`}
                            value={filterDatas.orderToNo}
                            autoComplete='off'
                            onChange={(e) => {
                                let number = CommonValidation.numberOnly(e.target.value);
                                if (number || e.target.value === "") {
                                    setFilterDatas((prevstate) => ({
                                        ...prevstate,
                                        orderToNo: e.target.value
                                    }))
                                }
                            }
                            }
                        />
                    </Box>
                    {/* Module Type */}
                    <Box component={"div"}>
                        <Box className="eMed_Filter_Header">
                            <Typography marginLeft={"1vw"}>Module Type</Typography>
                        </Box>
                        <Box className="eMed_CheckBox_Group">
                            {['IP', 'OP', 'RADIOLOGY', 'LAB', ...(localGetItem("Investigation_List") ? JSON.parse(localGetItem("Investigation_List")) : [])]?.map((type) => (
                                <FormControlLabel
                                    key={type}
                                    control={
                                        <Checkbox
                                            checked={filterDatas.module_type.includes(type)}
                                            onChange={() => handleCheckboxChange('module_type', type)}
                                        />
                                    }
                                    label={type.toUpperCase()}
                                />
                            ))}
                        </Box>
                    </Box>

                    {/* Status */}
                    <Box component={"div"}>
                        <Box className="eMed_Filter_Header">
                            <Typography marginLeft={"1vw"}>Status</Typography>
                        </Box>
                        <Box className="eMed_CheckBox_Group">
                            {['Pending', 'Billed', 'Accepted', 'Cancelled', 'Draft'].map((status) => (
                                <FormControlLabel
                                    key={status}
                                    control={
                                        <Checkbox
                                            checked={filterDatas.status.includes(status)}
                                            onChange={() => handleCheckboxChange('status', status)}
                                        />
                                    }
                                    label={status.charAt(0).toUpperCase() + status.slice(1)}
                                />
                            ))}
                        </Box>
                    </Box>

                    {/* Patient Type */}
                    <Box component={"div"}>
                        <Box className="eMed_Filter_Header">
                            <Typography marginLeft={"1vw"}>Patient Type</Typography>
                        </Box>
                        <Box className="eMed_CheckBox_Group">
                            {['ip', 'op', 'self'].map((type) => (
                                <FormControlLabel
                                    key={type}
                                    control={
                                        <Checkbox
                                            checked={filterDatas.Patient_type.includes(type)}
                                            onChange={() => handleCheckboxChange('Patient_type', type)}
                                        />
                                    }
                                    label={type.toUpperCase()}
                                />
                            ))}
                        </Box>
                    </Box>

                    {/* clear and search button */}
                    <Box component={"div"} className="eMed_Filter_Btn_Div">
                        <Stack direction="row" spacing={2} mt={2}>
                            <Button variant="outlined" onClick={handleFilterClear}>
                                {t("Clear")}
                            </Button>
                            <Button variant="contained" onClick={handleFilterApply}>
                                {t("Search")}
                            </Button>
                        </Stack>
                    </Box>
                </Box>
            </Box>
        )
    };

    const columns = [
        { field: "order_item_number", headerName: t("Order Number"), flex: 1.6,
            renderCell: (params) => (
                <Box>
                    {params?.row?.order_item_number?.length > 19 ?
                        <Tooltip placement='top' title={params?.row?.order_item_number} arrow>
                            <div>{params?.row?.order_item_number.slice(0, 17) + "..."}</div></Tooltip> :
                        params?.row?.order_item_number ? params?.row?.order_item_number : "-"}
                </Box>
            )
        },
        {
            field: "patient_name",
            flex: 1.6,
            headerName: t("PatientDetails"),
            renderCell: (params) => (
                <Box>
                    <CommonPatientDetails
                        disablePhoto={true}
                        data={params?.row}
                        fromCreditBill={true}
                    />
                </Box>
            ),
            valueGetter: (params) => {
                const name = params.row?.patient_name || "";
                const mobile = params.row?.patient_mobile_number || "";
                const UHID = params.row?.patient_account_number || "";
                return `${name} ${mobile} ${UHID}`;
            },
        },
        {
            field: "order_date",
            flex: 1,
            headerName: t("Date"),
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (
                <Box>
                    {params?.row?.order_date ? formatDate(params?.row?.order_date) : "-"}
                </Box>
            )
        },
        { field: "service_head_code", headerName: t("Service Id"), flex: 1 },
        {
            field: "service_name", headerName: t("Order Details"), flex: 2.5, renderCell: (params) => (
                <Box>
                    {params?.row?.service_name?.length > 35 ?
                        <Tooltip placement='top' title={params?.row?.service_name} arrow>
                            <div>{params?.row?.service_name.slice(0, 35) + "..."}</div></Tooltip> :
                        params?.row?.service_name ? params?.row?.service_name : "-"}
                </Box>
            )
        },
        { field: "service_type", headerName: t("Module Type"), flex: 1 },
        {
            field: "total_amount",
            flex: 1,
            headerName: `${t("Amount")} (${CurrencySymbol})`,
            type: "number",
            renderCell: (params) => (
                <Box sx={{ display: "flex", flexDirection:'column', justifyContent:'flex-end', alignItems:'flex-end' }}>
                    <Typography>{params?.row?.total_amount ? AmountFormat(params?.row?.total_amount) : "0.00"}</Typography>
                    <Typography sx={{ fontSize:"0.8vw"}}>{params?.row?.is_advance_adjusted ? "Advance Adjusted" : ""}</Typography>
                </Box>
            )
        },
        { field: "order_line_status", headerName: t("Status"), flex: 0.8 },
        { field: "created_by", headerName: t("Created By"), flex: 1 },
    ];

    return (
        <Box component="div" className="eMed_Main_container">

            {/* Stats and Filter Container */}
            <Box component="div" className="eMed_Top_Container">
                <Box component="div" className="eMed_Cards_Container">
                    {AmountsCard(t("Total Amount"), cardData?.totalAmount || 0)}
                    {AmountsCard(t("Pending"), cardData?.pending || 0)}
                    {AmountsCard(t("Billed"), cardData?.billed || 0)}
                    {AmountsCard(t("Accepted"), cardData?.accepted || 0)}
                    {AmountsCard(t("Cancelled"), cardData?.cancelled || 0)}
                </Box>

                {/* Filter and Print Actions */}
                <Box component="div" className="eMed_Action_Container">
                    <Box component="div" className="eMed_Filter_Icon_div">
                        <Tooltip title="Filter" placement="top" arrow>
                            <Button className="eMed_usrconf_btn" onClick={handleOpenFilter}>
                                <img src={ImagePaths.Filter.default} alt="filter" className="eMed_action_img" />
                            </Button>
                        </Tooltip>
                    </Box>
                    <Tooltip title="Print" placement="top" arrow>
                        <Button className="eMed_usrconf_btn" onClick={PrintData}>
                            <img src={ImagePaths.LabPrintIcon.default} alt="print" className="eMed_action_img" />
                        </Button>
                    </Tooltip>
                </Box>
            </Box>

            {/* Table Container */}
            <Box component="div" className="eMed_Table_Container">
                <Stack component="div">
                    <Paper className="eMed_Table_Wrapper">
                        <DataGrid
                            getRowId={(row, index) => `${row.order_item_number}-${index}`}
                            rows={orderList}
                            columns={columns}
                            page={page}
                            pageSize={pageSize}
                            hideFooterSelectedRowCount
                            localeText={{
                                toolbarColumns: "",
                                toolbarDensity: "",
                                toolbarExport: "",
                                toolbarFilters: "",
                                toolbarExportPrint: ""
                            }}
                            headerHeight={40}
                            components={{
                                Toolbar: gridToolBar,
                                NoRowsOverlay: () => (
                                    <Stack className="eMed_conf_nodata">
                                        {t("NoRecordsFound")}
                                    </Stack>
                                )
                            }}
                            rowsPerPageOptions={[10, 20, 30]}
                            disableSelectionOnClick
                            pagination
                            onPageChange={(newPage) => setPage(newPage)}
                            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                            loading={isLoader}
                        />
                    </Paper>
                </Stack>
            </Box>

            {/* Drawer for Filter */}
            <Drawer
                anchor="right"
                open={filterOpen}
                onClose={handleCloseFilter}
            >
                {Filter()}
            </Drawer>

            {isErrorMsg && (
                <ToastMsg
                    severity="error"
                    msg={isErrorMsgText}
                    msgPop={() => setIsErrorMsg(false)}
                />
            )}
        </Box>
    );
};

export default OrderBills;
